import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import moment from 'moment'
import { useCountUp } from './overviewTheme'

const GlassChip = ({ label, value, accent }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={{
      px: 1.5,
      py: 0.75,
      borderRadius: 5,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.14)',
      backdropFilter: 'blur(8px)',
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: accent ?? '#4DBBC4',
        flexShrink: 0,
      }}
    />
    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.95rem' }}>
      {value}
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.8rem' }}>
      {label}
    </Typography>
  </Stack>
)

// Slow-looping ECG trace along the bottom of the hero — decorative only
const PulseTrace = () => (
  <Box
    component="svg"
    viewBox="0 0 1200 60"
    preserveAspectRatio="none"
    aria-hidden="true"
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: 60,
      opacity: 0.5,
      '@media (prefers-reduced-motion: reduce)': {
        '& path': { animation: 'none' },
      },
    }}
  >
    <Box
      component="path"
      d="M0,40 L180,40 L200,40 L212,16 L224,52 L236,40 L420,40 L440,40 L452,10 L464,56 L476,40 L680,40 L700,40 L712,18 L724,50 L736,40 L940,40 L960,40 L972,12 L984,54 L996,40 L1200,40"
      fill="none"
      stroke="#4DBBC4"
      strokeWidth="1.5"
      strokeLinejoin="round"
      strokeLinecap="round"
      sx={{
        strokeDasharray: 2400,
        strokeDashoffset: 2400,
        animation: 'anPulseDraw 9s linear infinite',
        '@keyframes anPulseDraw': {
          '0%': { strokeDashoffset: 2400 },
          '70%': { strokeDashoffset: 0 },
          '100%': { strokeDashoffset: 0 },
        },
      }}
    />
  </Box>
)

const OverviewHero = ({ orgSettings, firstName, data }) => {
  const hour = moment().hour()
  const greeting =
    hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'

  const onDutyNow = useCountUp(data?.operations?.onDutyNow ?? 0)

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        px: { xs: 3, md: 5 },
        pt: { xs: 4, md: 5 },
        pb: { xs: 6, md: 7 },
        color: '#FFFFFF',
        background: 'linear-gradient(120deg, #1A2230 0%, #242F40 55%, #1E3A45 100%)',
      }}
    >
      {/* ambient glows + fine dot grid */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(600px 300px at 85% -20%, rgba(35, 154, 164, 0.35), transparent 70%),' +
            'radial-gradient(500px 260px at -10% 110%, rgba(204, 164, 59, 0.16), transparent 70%),' +
            'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: 'auto, auto, 22px 22px',
        }}
      />
      <PulseTrace />

      <Stack
        position="relative"
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={3}
      >
        <Stack gap={1}>
          <Typography
            sx={{
              color: '#4DBBC4',
              fontWeight: 600,
              fontSize: '0.78rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {orgSettings?.orgName ?? 'Company'} · Command Briefing
          </Typography>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: { xs: '1.7rem', md: '2.2rem' },
              lineHeight: 1.15,
            }}
          >
            {greeting}
            {firstName ? `, ${firstName}` : ''}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            {moment().format('dddd D MMMM YYYY')} — here's where the
            organisation stands right now.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
            <GlassChip
              label="shifts today"
              value={data?.operations?.shiftsToday ?? 0}
            />
            <GlassChip
              label="shifts next 7 days"
              value={data?.operations?.shiftsNext7 ?? 0}
            />
            <GlassChip
              label="open incidents"
              value={data?.governance?.openIncidents ?? 0}
              accent={
                (data?.governance?.openIncidents ?? 0) > 0
                  ? '#F28B1F'
                  : '#4DBBC4'
              }
            />
            <GlassChip
              label="fleet operational"
              value={`${data?.fleet?.operationalVehicles ?? 0}/${
                data?.fleet?.totalVehicles ?? 0
              }`}
            />
          </Stack>
        </Stack>

        <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0}>
          <Typography
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '3.4rem', md: '4.4rem' },
              lineHeight: 1,
              color: '#FFFFFF',
            }}
          >
            {onDutyNow}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            crew{onDutyNow === 1 ? '' : 's'} on duty right now
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}

export default OverviewHero
